import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';


export class Slideshow extends Component {
  static get observedAttributes() {
    return['initial-slide', 'infinite'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        if (!this.isConnected || !this.mainSwiper) return;
        const index = parseInt(newValue, 10) || 0;
        
        // Find slide based on new index
        const slides = this.refs.slides || Array.from(this.querySelectorAll('slideshow-slide'));
        const slide = slides[index];
        const slide_id = slide?.getAttribute('slide-id');
        
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs =['slideshowContainer'];

  async connectedCallback() {
    super.connectedCallback();

    // 1. Gather all elements from the DOM based on your exact HTML structure
    this.mainComponent = this; // The <slideshow-component> itself
    this.mainContainer = this.refs.slideshowContainer || this.querySelector('slideshow-container');
    this.mainWrapper = this.refs.scroller || this.querySelector('slideshow-slides');
    this.mainSlides = this.refs.slides || Array.from(this.querySelectorAll('slideshow-slide'));

    this.arrowsContainer = this.querySelector('slideshow-arrows');
    this.prevBtn = this.refs.previous || this.querySelector('.slideshow-control--previous');
    this.nextBtn = this.refs.next || this.querySelector('.slideshow-control--next');

    // Thumbnails logic
    this.thumbContainer = this.querySelector('scroll-hint.slideshow-controls__thumbnails-container');
    this.thumbWrapper = this.querySelector('.slideshow-controls__thumbnails');
    this.thumbSlides = Array.from(this.querySelectorAll('.slideshow-controls__thumbnail'));

    // Mobile Dots logic
    this.mobileDotsContainer = this.querySelector('.slideshow-controls__dots');

    if (!this.mainSlides || this.mainSlides.length === 0) return;

    // --- NEW: Reposition arrows for mobile before initializing Swiper ---
    this.#repositionArrowsForMobile();
    
    // Add event listener in case the user rotates the device or resizes window
    this.resizeListener = () => this.#repositionArrowsForMobile();
    window.addEventListener('resize', this.resizeListener);

    // 2. Safely apply Swiper specific CSS classes
    this.#applySwiperClasses();

    // 3. Initialize Swiper after layout is ready
    requestAnimationFrame(() => {
      this.#initSwiper();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.mainSwiper) this.mainSwiper.destroy(true, true);
    if (this.thumbSwiper) this.thumbSwiper.destroy(true, true);
    window.removeEventListener('resize', this.resizeListener);
  }

  /**
   * REPOSITION ARROWS FOR MOBILE
   * Moves the <slideshow-arrows> out of the swiper container and to the bottom
   * of the <slideshow-component> if screen is <= 768px.
   */
  #repositionArrowsForMobile() {
    if (!this.arrowsContainer) return;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    if (isMobile) {
      // Move arrows to the very end of <slideshow-component>
      if (this.arrowsContainer.parentElement !== this.mainComponent) {
        this.mainComponent.appendChild(this.arrowsContainer);
      }
    } else {
      // Put them back inside <slideshow-container> for desktop
      if (this.arrowsContainer.parentElement !== this.mainContainer && this.mainContainer) {
        // Typically arrows are at the beginning or absolute in the container
        this.mainContainer.insertBefore(this.arrowsContainer, this.mainContainer.firstChild);
      }
    }
  }

  #applySwiperClasses() {
    // --- MAIN SLIDER CLASSES ---
    if (this.mainContainer) {
      this.mainContainer.classList.add('swiper');
      this.mainContainer.style.setProperty('overflow', 'hidden', 'important');
    }
    
    if (this.mainWrapper) {
      this.mainWrapper.classList.add('swiper-wrapper');
      // Override Theme's default CSS grid/snap that breaks Swiper
      Object.assign(this.mainWrapper.style, {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        overflow: 'visible',
        gap: '0px'
      });
    }

    if (this.mainSlides) {
      this.mainSlides.forEach(slide => {
        slide.classList.add('swiper-slide');
        slide.removeAttribute('hidden');
        slide.setAttribute('aria-hidden', 'false');
        
        Object.assign(slide.style, {
          display: 'block', 
          flexShrink: '0',
          width: '100%',
          position: 'relative'
        });
      });
    }

    // --- THUMBNAIL CLASSES (PREVIEW GALLERY) ---
    if (this.thumbContainer && this.thumbWrapper && this.thumbSlides.length > 0) {
      this.thumbContainer.classList.add('swiper');
      this.thumbContainer.style.setProperty('overflow', 'hidden', 'important');
      
      this.thumbWrapper.classList.add('swiper-wrapper');
      Object.assign(this.thumbWrapper.style, {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        gap: '0px' 
      });

      this.thumbSlides.forEach(slide => {
        slide.classList.add('swiper-slide');
        slide.style.flexShrink = '0';
      });
    }
  }

  #initSwiper() {
    const isInfinite = this.getAttribute('infinite') !== null;

    // 1. INIT THUMBNAIL SWIPER
    if (this.thumbContainer && this.thumbSlides.length > 0) {
      this.thumbSwiper = new Swiper(this.thumbContainer, {
        slidesPerView: 'auto',
        spaceBetween: 8, 
        freeMode: true,
        direction: 'vertical',
        preventClicks: true,
        watchSlidesProgress: true,
      });
    }

    // 2. INIT MAIN SWIPER
    this.mainSwiper = new Swiper(this.mainContainer, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: isInfinite,
      preventClicks: true,      
      preventClicksPropagation: true,
      
      // Arrows (prev/next)
      navigation: {
        nextEl: this.nextBtn || null,
        prevEl: this.prevBtn || null,
      },

      // Mobile pagination (dots) replacing theme's <ol>
      pagination: {
        el: this.mobileDotsContainer ? this.mobileDotsContainer.parentElement : null,
        clickable: true,
      },

      // Connect thumbnails
      thumbs: {
        swiper: this.thumbSwiper || null
      },

      on: {
        slideChange: (swiper) => {
          const index = swiper.realIndex;
          const slide = this.mainSlides[index];

          if (slide) {
            this.dispatchEvent(
              new SlideshowSelectEvent({
                index: index,
                previousIndex: swiper.previousIndex,
                userInitiated: true,
                trigger: 'scroll',
                slide: slide,
                id: slide.getAttribute('slide-id'),
              })
            );
          }
        },
        sliderMove: function() {
          this.el.classList.add('is-swiping');
        },
      
        touchEnd: function() {
          setTimeout(() => {
            this.el.classList.remove('is-swiping');
          }, 50);
        },
      }
    });

    // Go to initial slide
    const initialIndex = parseInt(this.getAttribute('initial-slide'), 10) || 0;
    if (initialIndex > 0) {
      if (isInfinite) {
        this.mainSwiper.slideToLoop(initialIndex, 0);
      } else {
        this.mainSwiper.slideTo(initialIndex, 0);
      }
    }
  }

  async select(input, event, options = {}) {
    if (!this.mainSwiper || !this.mainSlides) return;

    let targetIndex = -1;

    if (typeof input === 'number') {
      targetIndex = input;
    } else if (typeof input === 'string') {
      targetIndex = parseInt(input, 10);
    } else if (input && input.id) {
      const requestedSlide = this.mainSlides.find((slide) => slide.getAttribute('slide-id') == input.id);
      if (requestedSlide) {
        targetIndex = this.mainSlides.indexOf(requestedSlide);
      }
    }

    if (targetIndex !== -1) {
      const speed = options.animate === false ? 0 : 300;
      const isInfinite = this.getAttribute('infinite') !== null;
      
      if (isInfinite) {
        this.mainSwiper.slideToLoop(targetIndex, speed);
      } else {
        this.mainSwiper.slideTo(targetIndex, speed);
      }
    }
  }

  next(event) {
    event?.preventDefault();
    this.mainSwiper?.slideNext();
  }

  previous(event) {
    event?.preventDefault();
    this.mainSwiper?.slidePrev();
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}